/**
 * useUserRoles Hook
 *
 * Mutations for managing user roles/profiles.
 *
 * @module features/users/hooks/useUserRoles
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  assignRole,
  removeRole,
} from '@/features/users/services/businessAccountUsers';
import type {
  AssignRoleRequest,
  AssignRoleResponse,
  RemoveRoleRequest,
  RemoveRoleResponse,
} from '@/features/users/types/users.types';
import { BUSINESS_ACCOUNT_USERS_QUERY_KEY } from './useBusinessAccountUsers';

/**
 * Hook for assigning a role to a user
 *
 * @returns Mutation object for assigning roles
 *
 * @example
 * ```tsx
 * const { mutateAsync: assignUserRole, isPending } = useAssignRole();
 *
 * const handleAssign = async () => {
 *   await assignUserRole({ userId: 'user-123', roleName: 'ROLE_SUPERVISOR' });
 *   message.success('Role assigned!');
 * };
 * ```
 */
export function useAssignRole() {
  const queryClient = useQueryClient();

  return useMutation<AssignRoleResponse, Error, AssignRoleRequest>({
    mutationFn: assignRole,
    onSuccess: () => {
      // Invalidate users list to refetch with updated roles
      queryClient.invalidateQueries({
        queryKey: BUSINESS_ACCOUNT_USERS_QUERY_KEY,
      });
    },
  });
}

/**
 * Hook for removing a role from a user
 *
 * @returns Mutation object for removing roles
 *
 * @example
 * ```tsx
 * const { mutateAsync: removeUserRole, isPending } = useRemoveRole();
 *
 * const handleRemove = async () => {
 *   await removeUserRole({ userId: 'user-123', roleName: 'ROLE_SUPERVISOR' });
 *   message.success('Role removed!');
 * };
 * ```
 */
export function useRemoveRole() {
  const queryClient = useQueryClient();

  return useMutation<RemoveRoleResponse, Error, RemoveRoleRequest>({
    mutationFn: removeRole,
    onSuccess: () => {
      // Invalidate users list to refetch with updated roles
      queryClient.invalidateQueries({
        queryKey: BUSINESS_ACCOUNT_USERS_QUERY_KEY,
      });
    },
  });
}
